import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Search } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import type { AdminPromotionRow } from '@repo/shared-types';
import { Input } from '@repo/ui/src/components/ui/input';
import { Button } from '@repo/ui/src/components/ui/button';
import { DataTable, type DataTableColumnMeta } from '../components/DataTable';
import { PromotionStatusBadge } from '../components/PromotionStatusBadge';
import { usePromotions } from '../hooks/usePromotions';
import { formatVnDateTime } from '../lib/adminFormat';
import { PromotionSheet } from './components/PromotionSheet';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 50;
const DEFAULT_SORT = 'createdAt:desc';

const columns: ColumnDef<AdminPromotionRow, unknown>[] = [
  {
    id: 'name',
    header: 'Tên',
    meta: { sortField: 'name' } satisfies DataTableColumnMeta,
    cell: ({ row }) => (
      <Link to={`/promotions/${row.original.id}`} className="font-medium text-surface-900 hover:underline">
        {row.original.name}
      </Link>
    ),
  },
  {
    id: 'code',
    header: 'Mã',
    cell: ({ row }) =>
      row.original.code ? (
        <span className="font-mono text-sm tabular-nums text-surface-800">{row.original.code}</span>
      ) : (
        <span className="text-surface-500">Chiến dịch</span>
      ),
  },
  {
    id: 'percentOff',
    header: 'Giảm',
    meta: { headerClassName: 'text-right', cellClassName: 'text-right tabular-nums' } satisfies DataTableColumnMeta,
    cell: ({ row }) => `${row.original.percentOff}%`,
  },
  {
    id: 'window',
    header: 'Thời gian',
    cell: ({ row }) => (
      <span className="whitespace-nowrap tabular-nums text-surface-600">
        {formatVnDateTime(row.original.startsAt)} → {formatVnDateTime(row.original.endsAt)}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Trạng thái',
    cell: ({ row }) => (
      <PromotionStatusBadge
        active={row.original.active}
        startsAt={row.original.startsAt}
        endsAt={row.original.endsAt}
      />
    ),
  },
  {
    id: 'redemptionCount',
    header: 'Lượt dùng',
    meta: { headerClassName: 'text-right', cellClassName: 'text-right tabular-nums' } satisfies DataTableColumnMeta,
    cell: ({ row }) =>
      row.original.maxRedemptions != null
        ? `${row.original.redemptionCount}/${row.original.maxRedemptions}`
        : row.original.redemptionCount,
  },
];

export function PromotionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);

  const page = Number(searchParams.get('page')) || 1;
  const limit = Number(searchParams.get('limit')) || DEFAULT_LIMIT;
  const sort = searchParams.get('sort') || DEFAULT_SORT;
  const q = searchParams.get('q') ?? '';

  const [searchInput, setSearchInput] = useState(q);

  // Keep the box in sync when `q` changes from outside a keystroke here (e.g.
  // browser Back/Forward, or a fresh deep link) — otherwise the box can show
  // stale text that no longer matches what the table is actually filtered by.
  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  // Debounce search input → URL (resets to page 1 on a real change only).
  useEffect(() => {
    if (searchInput === q) return;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (searchInput) next.set('q', searchInput);
      else next.delete('q');
      next.set('page', '1');
      setSearchParams(next, { replace: true });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  const params = useMemo(() => ({ page, limit, sort, q: q || undefined }), [page, limit, sort, q]);
  const { data, isLoading, error } = usePromotions(params);

  const setPage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(nextPage));
    setSearchParams(next, { replace: true });
  };

  const setSort = (nextSort: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('sort', nextSort);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-surface-900">Ưu đãi</h1>
          <p className="text-sm text-muted-foreground mt-1">Các khuyến mãi giảm giá theo phần trăm trên nền tảng.</p>
        </div>
        <Button type="button" onClick={() => setCreateOpen(true)}>
          Tạo ưu đãi
        </Button>
      </div>

      <PromotionSheet mode="create" open={createOpen} onOpenChange={setCreateOpen} />

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Tìm theo tên hoặc mã..."
          className="pl-9"
        />
      </div>

      <DataTable
        columns={columns}
        data={data?.rows ?? []}
        total={data?.total ?? 0}
        page={page}
        limit={limit}
        sort={sort}
        onPageChange={setPage}
        onSortChange={setSort}
        isLoading={isLoading}
        error={error}
        emptyText={q ? 'Không tìm thấy ưu đãi phù hợp.' : 'Chưa có ưu đãi nào.'}
      />
    </div>
  );
}
